import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { hourly } from "./provider";

const syncModuleId = "dashboard-layout";

export type DashboardTileKey =
  | "aktiverEinsatz"
  | "linkTiles"
  | "infos"
  | "wetter"
  | "sperrungen"
  | "informationen"
  | "warnkarte"
  | "lardis";

export type DashboardLayoutTile = {
  readonly key: DashboardTileKey;
  readonly column: 0 | 1 | 2;
  readonly order: number;
  readonly visible: boolean;
};

export type DashboardLayout = {
  readonly tiles: ReadonlyArray<DashboardLayoutTile>;
  /**
   * Hidden quick access ("linkTiles") entries: module ids (e.g. "kontakte") plus
   * "weblinks" (weblinks + external pages) and "crs". Missing = everything visible.
   */
  readonly hiddenLinkTiles?: ReadonlyArray<string>;
  /** Enabled opt-in quick access entries (hidden by default), e.g. "datenpflege". */
  readonly shownLinkTiles?: ReadonlyArray<string>;
};

// Mirrors the fixed 3-column layout this dashboard used before it became configurable
// (see rescuetablet-portal api/src/lib/dashboard-layout-default.ts) — used until the first
// successful sync (or when offline on first launch), so the dashboard is never empty.
export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  tiles: [
    { key: "aktiverEinsatz", column: 0, order: 0, visible: true },
    { key: "linkTiles", column: 0, order: 1, visible: true },
    { key: "infos", column: 0, order: 2, visible: true },
    { key: "wetter", column: 1, order: 0, visible: true },
    { key: "sperrungen", column: 1, order: 1, visible: true },
    { key: "informationen", column: 1, order: 2, visible: true },
    { key: "warnkarte", column: 2, order: 0, visible: true },
    { key: "lardis", column: 2, order: 1, visible: true },
  ],
};

function useDashboardLayoutSyncModule(): SyncModule<DashboardLayout | null> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: hourly } },
      // Dashboard layout is a new-Datenportal-only resource — the legacy portal has no
      // equivalent endpoint, so we simply don't fetch anything for it.
      fetchData:
        useNewApi && baseUrl
          ? () => fetchDashboardLayoutNew(portalKey, baseUrl)
          : async () => null,
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchDashboardLayoutNew(
  portalKey: string,
  baseUrl: string,
): Promise<DashboardLayout | null> {
  const response = await fetch(`${baseUrl}/api/device/sync/dashboard-layout`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return data.dashboardLayout ?? null;
}

export function useDashboardLayoutSync() {
  const module = useDashboardLayoutSyncModule();
  useSyncModuleRegistration(module);
}

export function useDashboardLayout(): DashboardLayout {
  return useSyncData(syncModuleId) ?? DEFAULT_DASHBOARD_LAYOUT;
}
